/**
 * Notifications inbox context and hook.
 *
 * Hydrates the unified inbox feed (notification rows + read-time-projected Space
 * `@here`/`@role` mentions) from the API, keeps the bell badge in sync, and
 * overlays live events. The badge counts items that are both newer than the
 * durable seen cursor and still unread, so reading a conversation or Space
 * channel shrinks it without opening the list.
 *   - `notification_created` → prepend the row. Bump the badge only when the
 *     stored row is unread. Thread activity for a conversation or Space channel
 *     the member is already reading is stored read (and marked read on the
 *     server) and does not bump the badge.
 *   - `space_message` → debounced badge refresh (group mentions are derived
 *     server-side, so we let the authoritative count catch them up rather than
 *     re-deriving mentions on the client).
 *
 * "Clear history" empties the client feed, deletes durable notification rows,
 * and stores a per-identity id watermark so projected mentions cleared from the
 * UI do not reappear on the next hydrate.
 *
 * Guest-safe: `useInbox` returns an inert value when no provider is mounted, so
 * the public shell (which never mounts this) renders nothing inbox-related.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  createApiClient,
  INBOX_UNSEEN_COUNT_CAP,
  type ChatIncomingMessage,
  type InboxItem,
} from '@adieuu/shared';
import { useAppConfig } from '../config';
import { useIdentity } from './useIdentity';
import { useChatSocket } from './useChatSocket';

export interface InboxContextValue {
  /** Feed items, newest-first (notification rows + projected Space mentions). */
  items: InboxItem[];
  /** Bell badge: unseen items that are still unread, capped. */
  unseenCount: number;
  /** When the member last opened the inbox (ISO), or null. */
  seenAt: string | null;
  /** Whether the initial hydrate is in flight. */
  isLoading: boolean;
  /** Whether an older page is available. */
  hasMore: boolean;
  /** Load the next (older) page and append it. */
  loadMore: () => Promise<void>;
  /** Re-hydrate the feed + badge from the server. */
  refresh: () => Promise<void>;
  /** Re-fetch the bell badge (unseen and still unread) from the server. */
  refreshUnseen: () => Promise<void>;
  /** Advance the seen cursor to now and clear the badge. */
  markSeen: () => Promise<void>;
  /** Mark one inbox item read (optimistic; durable for notification rows). */
  markItemRead: (itemId: string) => Promise<void>;
  /** Mark every loaded item read + all notification rows on the server. */
  markAllRead: () => Promise<void>;
  /**
   * Optimistically mark feed rows tied to a Space channel as read (e.g. after
   * the channel's read cursor advances). Durable mention and reply rows are
   * reconciled server-side by `markChannelRead`. When `uptoMessageId` is set,
   * later live rows for that channel at or before the cursor are stored read.
   */
  markItemsReadForChannel: (channelId: string, uptoMessageId?: string) => void;
  /**
   * Optimistically mark thread notifications for a conversation as read.
   * When `uptoMessageId` is set, only rows at or before that message are
   * flipped, and later live rows within the cursor are stored read. Durable
   * rows are reconciled by `markConversationThreadRead`.
   */
  markItemsReadForConversation: (conversationId: string, uptoMessageId?: string) => void;
  /**
   * Conversation the member is currently reading (active, focused, at the
   * bottom). Incoming thread notifications for it are stored read. `null`
   * clears the live target without un-reading rows already cleared.
   */
  setAutoReadConversation: (conversationId: string | null) => void;
  /**
   * Space channel the member is currently reading (active channel, focused).
   * Incoming mention/reply notifications for it are stored read.
   */
  setAutoReadChannel: (channelId: string | null) => void;
  /**
   * Clear the inbox history from the client (and delete durable notification
   * rows). Projected Space mentions at or before the clear are hidden via a
   * local watermark so they do not refill the list.
   */
  clearHistory: () => Promise<void>;
}

const InboxContext = createContext<InboxContextValue | null>(null);

/** Debounce window for badge refreshes triggered by Space message traffic. */
const SPACE_MESSAGE_REFRESH_DEBOUNCE_MS = 4000;

const inboxClearedBeforeKey = (identityId: string) => `adieuu:inboxClearedBefore:${identityId}`;

function readClearedBefore(identityId: string | undefined): string | null {
  if (!identityId) return null;
  try {
    return localStorage.getItem(inboxClearedBeforeKey(identityId));
  } catch {
    return null;
  }
}

function writeClearedBefore(identityId: string, itemId: string): void {
  try {
    localStorage.setItem(inboxClearedBeforeKey(identityId), itemId);
  } catch {
    /* quota / SSR */
  }
}

/** Inbox ids are ObjectId hex (chronological); keep only items newer than the watermark. */
function filterClearedHistory<T extends { id: string }>(
  items: T[],
  clearedBefore: string | null,
): T[] {
  if (!clearedBefore) return items;
  return items.filter((item) => item.id > clearedBefore);
}

/** DM / reaction rows cleared when that conversation is read. */
const CONVERSATION_THREAD_TYPES = new Set([
  'conversation_message',
  'conversation_message_reply',
  'message_reaction',
]);

/** Space rows cleared when that channel is read. */
const CHANNEL_THREAD_TYPES = new Set(['space_message_mention', 'space_message_reply']);

function dataString(data: Record<string, unknown>, key: string): string | undefined {
  const value = data[key];
  return typeof value === 'string' ? value : undefined;
}

/** ObjectId hex is chronological, so a string compare matches `_id <= upto`. */
/** True when the item is newer than the inbox seen cursor (or the inbox was never opened). */
function isUnseen(item: InboxItem, seenAt: string | null): boolean {
  if (!seenAt) return true;
  return item.createdAt > seenAt;
}

/**
 * Rows whose flip from unread → read should shrink the badge. Ids already
 * claimed (a second mark in the same tick, before React re-renders) are skipped
 * so the same row is not subtracted twice.
 */
function claimBadgeClears(
  items: readonly InboxItem[],
  seenAt: string | null,
  cleared: Set<string>,
): number {
  let n = 0;
  for (const item of items) {
    if (item.read || !isUnseen(item, seenAt) || cleared.has(item.id)) continue;
    cleared.add(item.id);
    n += 1;
  }
  return n;
}

function atOrBefore(messageId: string | undefined, upto: string | undefined): boolean {
  if (!upto) return true;
  if (!messageId) return false;
  return messageId <= upto;
}

function advanceCursor(cursors: Record<string, string>, key: string, messageId: string): void {
  const prev = cursors[key];
  if (!prev || messageId > prev) cursors[key] = messageId;
}

function conversationRowMatches(
  item: InboxItem,
  conversationId: string,
  uptoMessageId?: string,
): boolean {
  if (item.kind !== 'notification' || !CONVERSATION_THREAD_TYPES.has(item.type)) return false;
  if (dataString(item.data, 'conversationId') !== conversationId) return false;
  return atOrBefore(dataString(item.data, 'messageId'), uptoMessageId);
}

function channelRowMatches(item: InboxItem, channelId: string, uptoMessageId?: string): boolean {
  if (item.kind === 'space_mention') {
    return item.channelId === channelId && atOrBefore(item.id, uptoMessageId);
  }
  if (item.kind !== 'notification' || !CHANNEL_THREAD_TYPES.has(item.type)) return false;
  if (dataString(item.data, 'channelId') !== channelId) return false;
  return atOrBefore(dataString(item.data, 'messageId'), uptoMessageId);
}

function liveThreadAlreadyRead(
  type: string,
  data: Record<string, unknown>,
  auto: { conversationId: string | null; channelId: string | null },
  conversationThrough: Record<string, string>,
  channelThrough: Record<string, string>,
): boolean {
  if (CONVERSATION_THREAD_TYPES.has(type)) {
    const conversationId = dataString(data, 'conversationId');
    if (!conversationId) return false;
    if (auto.conversationId === conversationId) return true;
    const upto = conversationThrough[conversationId];
    return !!upto && atOrBefore(dataString(data, 'messageId'), upto);
  }
  if (CHANNEL_THREAD_TYPES.has(type)) {
    const channelId = dataString(data, 'channelId');
    if (!channelId) return false;
    if (auto.channelId === channelId) return true;
    const upto = channelThrough[channelId];
    return !!upto && atOrBefore(dataString(data, 'messageId'), upto);
  }
  return false;
}

export interface InboxProviderProps {
  children: ReactNode;
}

export function InboxProvider({ children }: InboxProviderProps) {
  const { apiBaseUrl } = useAppConfig();
  const { status: identityStatus, identity } = useIdentity();
  const { subscribe, onStateChange } = useChatSocket();

  const [items, setItems] = useState<InboxItem[]>([]);
  const [unseenCount, setUnseenCount] = useState(0);
  const [seenAt, setSeenAt] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const isLoggedIn = identityStatus === 'logged_in' && !!identity;
  const api = useMemo(() => createApiClient({ baseUrl: apiBaseUrl }), [apiBaseUrl]);

  const loadingMoreRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Mirror the badge so markSeen can read the pre-clear count synchronously for
  // rollback without adding unseenCount to its dependency list.
  const unseenCountRef = useRef(0);
  unseenCountRef.current = unseenCount;
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const clearedBeforeRef = useRef<string | null>(null);
  const autoReadRef = useRef<{ conversationId: string | null; channelId: string | null }>({
    conversationId: null,
    channelId: null,
  });
  const conversationReadThroughRef = useRef<Record<string, string>>({});
  const channelReadThroughRef = useRef<Record<string, string>>({});
  const seenAtRef = useRef<string | null>(null);
  seenAtRef.current = seenAt;
  /** Unseen rows already subtracted from the badge, so a second mark is a no-op. */
  const badgeClearedIdsRef = useRef(new Set<string>());
  const cursorIdentityRef = useRef(identity?.id);
  if (cursorIdentityRef.current !== identity?.id) {
    cursorIdentityRef.current = identity?.id;
    autoReadRef.current = { conversationId: null, channelId: null };
    conversationReadThroughRef.current = {};
    channelReadThroughRef.current = {};
    badgeClearedIdsRef.current.clear();
  }

  // Load the per-identity clear watermark when the alias changes (not every render —
  // clearHistory also writes the ref synchronously for the current session).
  useEffect(() => {
    clearedBeforeRef.current = identity?.id ? readClearedBefore(identity.id) : null;
  }, [identity?.id]);

  const refreshUnseen = useCallback(async () => {
    // Best-effort: a failed refresh leaves the last known badge and retries on
    // the next event/reconnect (mirrors useSpaceUnreadSync's syncAll).
    try {
      const res = await api.notifications.getInboxUnseenCount();
      if (res.success && res.data) {
        badgeClearedIdsRef.current.clear();
        setUnseenCount(res.data.unseen);
      }
    } catch {
      // Swallow client errors; the badge is eventually reconciled.
    }
  }, [api]);

  const refresh = useCallback(async () => {
    try {
      const [feed] = await Promise.all([api.notifications.getInbox(), refreshUnseen()]);
      if (feed.success && feed.data) {
        const clearedBefore = clearedBeforeRef.current;
        const visible = filterClearedHistory(feed.data.items, clearedBefore);
        setItems(visible);
        // If the whole page was below the watermark, stop paging older history.
        setNextCursor(
          clearedBefore && visible.length === 0 ? null : feed.data.nextCursor,
        );
        setSeenAt(feed.data.seenAt);
      }
    } catch {
      // Swallow client errors; hydrate retries on the next reconnect.
    }
  }, [api, refreshUnseen]);

  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current || !nextCursor) return;
    loadingMoreRef.current = true;
    try {
      const res = await api.notifications.getInbox({ cursor: nextCursor });
      if (res.success && res.data) {
        const clearedBefore = clearedBeforeRef.current;
        const page = filterClearedHistory(res.data.items, clearedBefore);
        setItems((prev) => {
          const seen = new Set(prev.map((i) => i.id));
          return [...prev, ...page.filter((i) => !seen.has(i.id))];
        });
        setNextCursor(clearedBefore && page.length === 0 ? null : res.data.nextCursor);
      }
    } catch {
      // Swallow client errors; the user can retry loading more.
    } finally {
      loadingMoreRef.current = false;
    }
  }, [api, nextCursor]);

  const markSeen = useCallback(async () => {
    // Optimistically clear the badge; the server cursor is monotonic so a stale
    // ack can never resurrect it. Restore the prior count if the ack fails so we
    // don't hide genuinely unseen activity.
    const previous = unseenCountRef.current;
    setUnseenCount(0);
    try {
      const res = await api.notifications.markInboxSeen();
      if (res.success && res.data) {
        badgeClearedIdsRef.current.clear();
        setSeenAt(res.data.seenAt);
      } else {
        setUnseenCount(previous);
      }
    } catch {
      setUnseenCount(previous);
    }
  }, [api]);

  const markItemsReadLocal = useCallback((predicate: (item: InboxItem) => boolean) => {
    const n = claimBadgeClears(
      itemsRef.current.filter(predicate),
      seenAtRef.current,
      badgeClearedIdsRef.current,
    );
    if (n > 0) setUnseenCount((c) => Math.max(0, c - n));
    setItems((prev) => {
      let changed = false;
      const next = prev.map((item) => {
        if (item.read || !predicate(item)) return item;
        changed = true;
        return { ...item, read: true };
      });
      return changed ? next : prev;
    });
  }, []);

  const markItemRead = useCallback(
    async (itemId: string) => {
      const target = itemsRef.current.find((item) => item.id === itemId);
      if (!target || target.read) return;
      const n = claimBadgeClears([target], seenAtRef.current, badgeClearedIdsRef.current);
      if (n > 0) setUnseenCount((c) => Math.max(0, c - n));
      setItems((prev) =>
        prev.map((item) => (item.id === itemId && !item.read ? { ...item, read: true } : item)),
      );
      if (target.kind !== 'notification') return;
      try {
        await api.notifications.markAsRead([itemId]);
      } catch {
        // Best-effort; a later refresh reconciles durable read state.
      }
    },
    [api],
  );

  const markAllRead = useCallback(async () => {
    markItemsReadLocal(() => true);
    try {
      await api.notifications.markAsRead('all');
    } catch {
      // Best-effort; a later refresh reconciles durable read state.
    }
  }, [api, markItemsReadLocal]);

  const markItemsReadForChannel = useCallback(
    (channelId: string, uptoMessageId?: string) => {
      if (uptoMessageId) advanceCursor(channelReadThroughRef.current, channelId, uptoMessageId);
      markItemsReadLocal((item) => channelRowMatches(item, channelId, uptoMessageId));
    },
    [markItemsReadLocal],
  );

  const markItemsReadForConversation = useCallback(
    (conversationId: string, uptoMessageId?: string) => {
      if (uptoMessageId) {
        advanceCursor(conversationReadThroughRef.current, conversationId, uptoMessageId);
      }
      markItemsReadLocal((item) => conversationRowMatches(item, conversationId, uptoMessageId));
    },
    [markItemsReadLocal],
  );

  const setAutoReadConversation = useCallback(
    (conversationId: string | null) => {
      autoReadRef.current.conversationId = conversationId;
      if (conversationId) markItemsReadForConversation(conversationId);
    },
    [markItemsReadForConversation],
  );

  const setAutoReadChannel = useCallback(
    (channelId: string | null) => {
      autoReadRef.current.channelId = channelId;
      if (channelId) markItemsReadForChannel(channelId);
    },
    [markItemsReadForChannel],
  );

  const clearHistory = useCallback(async () => {
    const newestId = itemsRef.current[0]?.id;
    const identityId = identity?.id;
    if (newestId && identityId) {
      writeClearedBefore(identityId, newestId);
      clearedBeforeRef.current = newestId;
    }
    setItems([]);
    setNextCursor(null);
    setUnseenCount(0);
    try {
      await api.notifications.deleteNotifications('all');
    } catch {
      // Local clear already applied; durable delete retries on the next clear.
    }
    try {
      const res = await api.notifications.markInboxSeen();
      if (res.success && res.data) setSeenAt(res.data.seenAt);
    } catch {
      // Badge already cleared locally.
    }
  }, [api, identity?.id]);

  // Keep live handlers stable across renders (subscribe once per connection).
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const refreshUnseenRef = useRef(refreshUnseen);
  refreshUnseenRef.current = refreshUnseen;

  const scheduleUnseenRefresh = useCallback(() => {
    if (debounceRef.current) return;
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      void refreshUnseenRef.current();
    }, SPACE_MESSAGE_REFRESH_DEBOUNCE_MS);
  }, []);

  // Initial hydrate + reset on auth / identity changes. Depending on the
  // identity id (not just login state) ensures switching accounts clears the
  // previous identity's feed before re-hydrating, rather than showing it stale.
  useEffect(() => {
    badgeClearedIdsRef.current.clear();
    if (!isLoggedIn) {
      setItems([]);
      setUnseenCount(0);
      setSeenAt(null);
      setNextCursor(null);
      return;
    }
    // Clear the prior identity's state so a switch doesn't flash stale data
    // while the new identity's inbox loads.
    setItems([]);
    setUnseenCount(0);
    setSeenAt(null);
    setNextCursor(null);
    setIsLoading(true);
    refresh().finally(() => setIsLoading(false));
  }, [isLoggedIn, identity?.id, refresh]);

  // Live overlay + reconnect re-hydrate.
  useEffect(() => {
    if (!isLoggedIn) return;
    const selfId = identity?.id;

    const unsubMessage = subscribe((message: ChatIncomingMessage) => {
      if (message.type === 'notification_created') {
        const n = (message as Extract<ChatIncomingMessage, { type: 'notification_created' }>).data
          .notification;
        const clearedBefore = clearedBeforeRef.current;
        if (clearedBefore && n.id <= clearedBefore) return;
        const alreadyRead = liveThreadAlreadyRead(
          n.type,
          n.data,
          autoReadRef.current,
          conversationReadThroughRef.current,
          channelReadThroughRef.current,
        );
        const item: InboxItem = {
          kind: 'notification',
          id: n.id,
          type: n.type,
          data: n.data,
          read: n.read || alreadyRead,
          createdAt: n.createdAt,
        };
        setItems((prev) => (prev.some((i) => i.id === item.id) ? prev : [item, ...prev]));
        // Already-read rows (viewing that thread, or the server stored them read)
        // must not inflate the badge.
        if (!item.read) {
          setUnseenCount((c) => Math.min(INBOX_UNSEEN_COUNT_CAP, c + 1));
        }
        // The row may have been created after the thread was marked read, so the
        // create payload is still unread. Persist the single id.
        if (alreadyRead && !n.read) {
          void api.notifications.markAsRead([n.id]).catch(() => {});
        }
        return;
      }
      if (message.type === 'space_message') {
        const msg = (message as Extract<ChatIncomingMessage, { type: 'space_message' }>).data
          .message;
        // Our own messages never mention us; skip to avoid needless refreshes.
        if (selfId && msg.fromIdentityId === selfId) return;
        scheduleUnseenRefresh();
      }
    });

    let connectedHandled = true;
    const unsubState = onStateChange((state) => {
      if (state === 'disconnected' || state === 'reconnecting') {
        connectedHandled = false;
        return;
      }
      if (state !== 'connected' || connectedHandled) return;
      connectedHandled = true;
      void refreshRef.current();
    });

    return () => {
      unsubMessage();
      unsubState();
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [isLoggedIn, identity?.id, subscribe, onStateChange, scheduleUnseenRefresh, api]);

  const value = useMemo<InboxContextValue>(
    () => ({
      items,
      unseenCount,
      seenAt,
      isLoading,
      hasMore: nextCursor !== null,
      loadMore,
      refresh,
      refreshUnseen,
      markSeen,
      markItemRead,
      markAllRead,
      markItemsReadForChannel,
      markItemsReadForConversation,
      setAutoReadConversation,
      setAutoReadChannel,
      clearHistory,
    }),
    [
      items,
      unseenCount,
      seenAt,
      isLoading,
      nextCursor,
      loadMore,
      refresh,
      refreshUnseen,
      markSeen,
      markItemRead,
      markAllRead,
      markItemsReadForChannel,
      markItemsReadForConversation,
      setAutoReadConversation,
      setAutoReadChannel,
      clearHistory,
    ],
  );

  return <InboxContext.Provider value={value}>{children}</InboxContext.Provider>;
}

const NOOP_INBOX: InboxContextValue = {
  items: [],
  unseenCount: 0,
  seenAt: null,
  isLoading: false,
  hasMore: false,
  loadMore: async () => {},
  refresh: async () => {},
  refreshUnseen: async () => {},
  markSeen: async () => {},
  markItemRead: async () => {},
  markAllRead: async () => {},
  markItemsReadForChannel: () => {},
  markItemsReadForConversation: () => {},
  setAutoReadConversation: () => {},
  setAutoReadChannel: () => {},
  clearHistory: async () => {},
};

export function useInbox(): InboxContextValue {
  return useContext(InboxContext) ?? NOOP_INBOX;
}
